<template>
  <Section id="templates" tone="muted" pad-y="xl" aria-labelledby="templates-heading">
    <Container>
      <div class="mb-10 flex flex-col gap-3">
        <p class="text-xs font-semibold tracking-[0.14em] text-accent-strong uppercase">
          {{ t('sections.templates.eyebrow') }}
        </p>
        <h2 id="templates-heading" class="text-3xl font-bold text-ink-strong md:text-4xl">
          {{ t('sections.templates.title') }}
        </h2>
        <p class="max-w-2xl text-base text-ink-muted md:text-lg">
          {{ t('sections.templates.subtitle') }}
        </p>
      </div>

      <div class="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <PromoTemplateCard
          v-for="entry in templates"
          :key="entry.item.id"
          :icon="entry.item.icon"
          :title="entry.title"
          :description="entry.description"
          :traits="entry.traits"
          :timeline="entry.timeline"
          :traits-label="t('sections.templates.traitsLabel')"
          :use-cta-label="t('sections.templates.useCta')"
          :timeline-label="t('sections.templates.timelineLabel')"
          :use-aria-label="`${entry.title} — ${t('sections.templates.useCta')}`"
          @use="openTemplate(entry.item)"
        />
      </div>
    </Container>

    <Modal :open="modalOpen" :title="modalTitle" size="md" @update:open="setOpen">
      <TemplateLeadModalBody
        v-if="activeTemplate"
        :template-id="activeTemplate.id"
        :template-title="activeTitle"
        @submitted="(r) => r.ok && setOpen(false)"
      />
    </Modal>
  </Section>
</template>

<script setup lang="ts">
/**
 * `HomePromoTemplates` — секция «Шаблоны промо».
 *
 * Карточки 8 композиционных шаблонов; каждая карточка — focusable
 * region. Клик / Enter / Space открывают модалку с `LeadForm`,
 * предзаполненной названием шаблона (`source: 'template.use'`).
 * На успешный submit модалка закрывается (toast показывает сама
 * `LeadForm` через `useToast`).
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Section from '@/components/primitives/Section.vue';
import Container from '@/components/primitives/Container.vue';
import Modal from '@/components/feedback/Modal.vue';
import PromoTemplateCard from '@/components/domain/PromoTemplateCard.vue';
import TemplateLeadModalBody from '@/components/domain/TemplateLeadModalBody.vue';
import { PROMO_TEMPLATES, type PromoTemplate } from '@/content/home-data';
import { useLocale } from '@/composables/useLocale';
import { pickLocalized, pickLocalizedArray } from '@/lib/i18n/localize';

const { t } = useI18n();
const { locale } = useLocale();

const activeTemplate = ref<PromoTemplate | null>(null);
const modalOpen = ref(false);

const templates = computed(() =>
  PROMO_TEMPLATES.map((tpl) => ({
    item: tpl,
    title: pickLocalized(tpl, 'title', locale.value),
    description: pickLocalized(tpl, 'description', locale.value),
    traits: pickLocalizedArray(tpl, 'traits', locale.value),
    timeline: pickLocalized(tpl, 'timeline', locale.value),
  })),
);

const activeTitle = computed(() =>
  activeTemplate.value ? pickLocalized(activeTemplate.value, 'title', locale.value) : '',
);

const modalTitle = computed(() =>
  activeTemplate.value ? t('sections.templates.modalTitle', { template: activeTitle.value }) : '',
);

function openTemplate(tpl: PromoTemplate): void {
  activeTemplate.value = tpl;
  modalOpen.value = true;
}

function setOpen(value: boolean): void {
  modalOpen.value = value;
  if (!value) {
    activeTemplate.value = null;
  }
}
</script>
